import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ideasAPI, commentsAPI, collaborationsAPI } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  Sparkles, 
  ThumbsUp, 
  MessageSquare, 
  Users,
  Eye,
  Clock,
  Target,
  TrendingUp,
  DollarSign,
  Megaphone,
  AlertTriangle,
  CheckCircle,
  Send,
  Plus,
  UserPlus,
  Flag,
  Download
} from 'lucide-react';

export const IdeaDetailPage = () => {
  const { id } = useParams();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [idea, setIdea] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [isSuggestion, setIsSuggestion] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [newMilestone, setNewMilestone] = useState({ title: '', description: '' });
  const [milestoneDialogOpen, setMilestoneDialogOpen] = useState(false);

  const isOwner = idea?.user_id === user?.id;
  const isCollaborator = idea?.collaborators?.includes(user?.id);
  const canEdit = isOwner || isCollaborator;

  useEffect(() => {
    loadIdea();
    loadComments();
  }, [id]);

  const loadIdea = async () => {
    try {
      const response = await ideasAPI.getById(id);
      setIdea(response.data);
    } catch (error) {
      toast.error('Failed to load idea');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadComments = async () => {
    try {
      const response = await commentsAPI.getByIdea(id);
      setComments(response.data);
    } catch (error) {
      console.error('Failed to load comments:', error);
    }
  };

  const handleGeneratePlan = async () => {
    setGenerating(true);
    toast.info('Generating business plan with AI...', { duration: 10000 });
    try {
      const response = await ideasAPI.generatePlan(id);
      setIdea(response.data);
      toast.success('Business plan generated successfully!');
    } catch (error) {
      toast.error('Failed to generate plan. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const response = await ideasAPI.exportPdf(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeTitle = idea.title.replace(/[^a-zA-Z0-9\s-_]/g, '').trim().slice(0, 50);
      link.download = `${safeTitle}_business_plan.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF exported successfully!');
    } catch (error) {
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  const handleUpvote = async () => {
    try {
      const response = await ideasAPI.upvote(id);
      setIdea(prev => ({ ...prev, upvotes: response.data.upvotes }));
      toast.success(response.data.message);
      refreshUser();
    } catch (error) {
      toast.error('Failed to upvote');
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmittingComment(true);
    try {
      await commentsAPI.create({
        content: newComment,
        idea_id: id,
        is_suggestion: isSuggestion
      });
      toast.success(isSuggestion ? 'Suggestion posted! +25 points' : 'Comment posted! +10 points');
      setNewComment('');
      setIsSuggestion(false);
      loadComments();
      refreshUser();
    } catch (error) {
      toast.error('Failed to post comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleUpvoteComment = async (commentId) => {
    try {
      const response = await commentsAPI.upvote(commentId);
      setComments(prev => prev.map(c => 
        c.id === commentId ? { ...c, upvotes: response.data.upvotes } : c
      ));
      refreshUser();
    } catch (error) {
      toast.error('Failed to upvote');
    }
  };

  const handleAcceptSuggestion = async (commentId) => {
    try {
      await commentsAPI.accept(commentId);
      toast.success('Suggestion accepted! Contributor earned +50 points');
      loadComments();
    } catch (error) {
      toast.error('Failed to accept suggestion');
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    try {
      await collaborationsAPI.invite({ idea_id: id, invitee_email: inviteEmail });
      toast.success('Invitation sent!');
      setInviteEmail('');
      setInviteDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    }
  };

  const handleAddMilestone = async () => {
    if (!newMilestone.title.trim()) return;
    try {
      const response = await ideasAPI.addMilestone(id, newMilestone);
      setIdea(response.data);
      setNewMilestone({ title: '', description: '' });
      setMilestoneDialogOpen(false);
      toast.success('Milestone added!');
    } catch (error) {
      toast.error('Failed to add milestone');
    }
  };

  const handleToggleMilestone = async (milestoneId, completed) => {
    try {
      await ideasAPI.updateMilestone(id, milestoneId, { completed: !completed });
      setIdea(prev => ({
        ...prev,
        milestones: prev.milestones.map(m => 
          m.id === milestoneId ? { ...m, completed: !completed } : m
        )
      }));
      toast.success(completed ? 'Milestone unmarked' : 'Milestone completed!');
    } catch (error) {
      toast.error('Failed to update milestone');
    }
  };

  const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!idea) return null;

  const plan = idea.business_plan;

  return (
    <div className="space-y-6" data-testid="idea-detail-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="secondary">{idea.category}</Badge>
            <Badge className={idea.status === 'planned' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}>
              {idea.status}
            </Badge>
          </div>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl text-slate-900" data-testid="idea-title">
            {idea.title}
          </h1>
          <div className="flex items-center gap-4 mt-3 text-sm text-slate-600">
            <span className="flex items-center gap-1">
              <Eye className="w-4 h-4" /> {idea.views} views
            </span>
            <span className="flex items-center gap-1">
              <ThumbsUp className="w-4 h-4" /> {idea.upvotes} upvotes
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" /> {new Date(idea.created_at).toLocaleDateString()}
            </span>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <Button 
            variant="outline" 
            onClick={handleUpvote}
            data-testid="upvote-btn"
          >
            <ThumbsUp className="w-4 h-4 mr-2" />
            Upvote
          </Button>
          
          {canEdit && (
            <>
              <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="invite-btn">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Invite
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Invite Collaborator</DialogTitle>
                    <DialogDescription>
                      Send an invitation to collaborate on this business idea.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <Input
                      placeholder="Enter email address"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      data-testid="invite-email-input"
                    />
                    <Button onClick={handleInvite} className="w-full" data-testid="send-invite-btn">
                      Send Invitation
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              {!plan && (
                <Button 
                  className="gradient-primary text-white"
                  onClick={handleGeneratePlan}
                  disabled={generating}
                  data-testid="generate-plan-btn"
                >
                  {generating ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Generating...
                    </span>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate Plan
                    </>
                  )}
                </Button>
              )}

              {plan && (
                <Button 
                  variant="outline"
                  onClick={handleExportPdf}
                  disabled={exporting}
                  data-testid="export-pdf-btn"
                >
                  {exporting ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin"></div>
                      Exporting...
                    </span>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Export PDF
                    </>
                  )}
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Description</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 font-document leading-relaxed">{idea.description}</p>
              {idea.target_market && (
                <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1">
                    <Target className="w-4 h-4" />
                    Target Market
                  </div>
                  <p className="text-slate-600 text-sm">{idea.target_market}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Business Plan */}
          {plan && (
            <Card data-testid="business-plan-section">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-indigo-500" />
                  AI-Generated Business Plan
                </CardTitle>
              </CardHeader>
              <CardContent className="business-plan-doc space-y-6">
                {/* Executive Summary */}
                {plan.executive_summary && (
                  <section>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">Executive Summary</h3>
                    <p className="text-slate-700">{plan.executive_summary}</p>
                  </section>
                )}

                {/* Problem & Solution */}
                {(plan.problem_statement || plan.solution) && (
                  <div className="grid md:grid-cols-2 gap-4">
                    {plan.problem_statement && (
                      <div className="p-4 bg-red-50 rounded-lg">
                        <h4 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" /> Problem
                        </h4>
                        <p className="text-red-700 text-sm">{plan.problem_statement}</p>
                      </div>
                    )}
                    {plan.solution && (
                      <div className="p-4 bg-green-50 rounded-lg">
                        <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" /> Solution
                        </h4>
                        <p className="text-green-700 text-sm">{plan.solution}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Market Analysis */}
                {plan.market_analysis && (
                  <section>
                    <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-blue-500" /> Market Analysis
                    </h3>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-600">Market Size</p>
                        <p className="font-medium text-slate-900">{plan.market_analysis.market_size}</p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-600">Trends</p>
                        <ul className="text-sm text-slate-900">
                          {plan.market_analysis.trends?.map((t, i) => (
                            <li key={i}>• {t}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-600">Competitors</p>
                        <ul className="text-sm text-slate-900">
                          {plan.market_analysis.competitors?.map((c, i) => (
                            <li key={i}>• {c}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </section>
                )}

                {/* Business Model */}
                {plan.business_model && (
                  <section>
                    <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-green-500" /> Business Model
                    </h3>
                    <div className="space-y-3">
                      {plan.business_model.revenue_streams && (
                        <div className="p-4 bg-slate-50 rounded-lg">
                          <p className="text-sm font-medium text-slate-700 mb-2">Revenue Streams</p>
                          <div className="flex flex-wrap gap-2">
                            {plan.business_model.revenue_streams.map((r, i) => (
                              <Badge key={i} variant="secondary">{r}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {plan.business_model.pricing_strategy && (
                        <p className="text-slate-700"><strong>Pricing:</strong> {plan.business_model.pricing_strategy}</p>
                      )}
                    </div>
                  </section>
                )}

                {/* Marketing Strategy */}
                {plan.marketing_strategy && (
                  <section>
                    <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <Megaphone className="w-5 h-5 text-orange-500" /> Marketing Strategy
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      {plan.marketing_strategy.channels && (
                        <div className="p-4 bg-slate-50 rounded-lg">
                          <p className="text-sm font-medium text-slate-700 mb-2">Channels</p>
                          <ul className="text-sm text-slate-900">
                            {plan.marketing_strategy.channels.map((c, i) => (
                              <li key={i}>• {c}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {plan.marketing_strategy.tactics && (
                        <div className="p-4 bg-slate-50 rounded-lg">
                          <p className="text-sm font-medium text-slate-700 mb-2">Tactics</p>
                          <ul className="text-sm text-slate-900">
                            {plan.marketing_strategy.tactics.map((t, i) => (
                              <li key={i}>• {t}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </section>
                )}

                {/* Financial Projections */}
                {plan.financial_projections && (
                  <section>
                    <h3 className="text-lg font-semibold text-slate-900 mb-3">Financial Projections</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {['year_1', 'year_2', 'year_3'].map((year, i) => (
                        plan.financial_projections[year] && (
                          <div key={year} className="p-4 bg-indigo-50 rounded-lg text-center">
                            <p className="text-sm text-indigo-600">Year {i + 1}</p>
                            <p className="font-impact text-xl text-indigo-700">{plan.financial_projections[year]}</p>
                          </div>
                        )
                      ))}
                      {plan.financial_projections.break_even && (
                        <div className="p-4 bg-green-50 rounded-lg text-center">
                          <p className="text-sm text-green-600">Break Even</p>
                          <p className="font-medium text-green-700">{plan.financial_projections.break_even}</p>
                        </div>
                      )}
                    </div>
                  </section>
                )}

                {/* Risks */}
                {plan.risks_and_mitigation && plan.risks_and_mitigation.length > 0 && (
                  <section>
                    <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-yellow-500" /> Risks & Mitigation
                    </h3>
                    <div className="space-y-3">
                      {plan.risks_and_mitigation.map((item, i) => (
                        <div key={i} className="p-4 bg-yellow-50 rounded-lg">
                          <p className="font-medium text-yellow-800">{item.risk}</p>
                          <p className="text-sm text-yellow-700 mt-1">Mitigation: {item.mitigation}</p>
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </CardContent>
            </Card>
          )}

          {/* Comments Section */}
          <Card data-testid="comments-section">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Comments & Suggestions ({comments.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Comment Form */}
              <form onSubmit={handleSubmitComment} className="space-y-3">
                <Textarea
                  placeholder="Share your thoughts or suggestions..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  className="min-h-[100px]"
                  data-testid="comment-input"
                />
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                    <Checkbox 
                      checked={isSuggestion}
                      onCheckedChange={setIsSuggestion}
                      data-testid="suggestion-checkbox"
                    />
                    <span>Mark as suggestion (+25 pts instead of +10)</span>
                  </label>
                  <Button 
                    type="submit" 
                    disabled={submittingComment || !newComment.trim()}
                    data-testid="submit-comment-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Post
                  </Button>
                </div>
              </form>

              {/* Comments List */}
              <div className="space-y-4 pt-4 border-t">
                {comments.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No comments yet. Be the first to contribute!</p>
                ) : (
                  comments.map((comment) => (
                    <div 
                      key={comment.id} 
                      className={`p-4 rounded-lg border ${comment.is_suggestion ? 'border-orange-200 bg-orange-50/50' : 'border-slate-200'}`}
                      data-testid={`comment-${comment.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex gap-3">
                          <Avatar className="w-8 h-8">
                            <AvatarFallback className="bg-indigo-100 text-indigo-600 text-xs">
                              {getInitials(comment.user_name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-900">{comment.user_name}</span>
                              {comment.is_suggestion && (
                                <Badge className="bg-orange-100 text-orange-700 text-xs">Suggestion</Badge>
                              )}
                              {comment.is_accepted && (
                                <Badge className="bg-green-100 text-green-700 text-xs">Accepted</Badge>
                              )}
                            </div>
                            <p className="text-slate-700 mt-1">{comment.content}</p>
                            <p className="text-xs text-slate-500 mt-2">
                              {new Date(comment.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleUpvoteComment(comment.id)}
                          >
                            <ThumbsUp className="w-4 h-4 mr-1" />
                            {comment.upvotes}
                          </Button>
                          {isOwner && comment.is_suggestion && !comment.is_accepted && (
                            <Button 
                              size="sm"
                              className="gradient-primary text-white"
                              onClick={() => handleAcceptSuggestion(comment.id)}
                            >
                              Accept
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Creator Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm text-slate-600">Created by</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <Avatar className="w-10 h-10">
                  <AvatarFallback className="bg-indigo-100 text-indigo-600">
                    {getInitials(idea.user_name)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium text-slate-900">{idea.user_name}</p>
                  <p className="text-sm text-slate-500">Idea Owner</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Milestones */}
          <Card data-testid="milestones-section">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Flag className="w-5 h-5" />
                Milestones
              </CardTitle>
              {canEdit && (
                <Dialog open={milestoneDialogOpen} onOpenChange={setMilestoneDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="ghost" data-testid="add-milestone-btn">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Milestone</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <Input
                        placeholder="Milestone title"
                        value={newMilestone.title}
                        onChange={(e) => setNewMilestone(prev => ({ ...prev, title: e.target.value }))}
                        data-testid="milestone-title-input"
                      />
                      <Textarea
                        placeholder="Description (optional)"
                        value={newMilestone.description}
                        onChange={(e) => setNewMilestone(prev => ({ ...prev, description: e.target.value }))}
                        data-testid="milestone-desc-input"
                      />
                      <Button onClick={handleAddMilestone} className="w-full" data-testid="save-milestone-btn">
                        Add Milestone
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </CardHeader>
            <CardContent>
              {idea.milestones?.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">No milestones yet</p>
              ) : (
                <div className="space-y-3">
                  {idea.milestones?.map((milestone, index) => (
                    <div 
                      key={milestone.id} 
                      className={`flex items-start gap-3 ${index < idea.milestones.length - 1 ? 'milestone-line' : ''}`}
                    >
                      <Checkbox 
                        checked={milestone.completed}
                        onCheckedChange={() => canEdit && handleToggleMilestone(milestone.id, milestone.completed)}
                        disabled={!canEdit}
                        className="mt-1"
                      />
                      <div className={milestone.completed ? 'opacity-60' : ''}>
                        <p className={`font-medium text-slate-900 ${milestone.completed ? 'line-through' : ''}`}>
                          {milestone.title}
                        </p>
                        {milestone.description && (
                          <p className="text-sm text-slate-600">{milestone.description}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Collaborators */}
          {idea.collaborators?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Collaborators
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600">
                  {idea.collaborators.length} collaborator(s) on this project
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default IdeaDetailPage;
